import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { getConversationPermission, type PermissaoConversa } from '@/lib/whatsappConversationActions';
import type { Conversation } from '@/hooks/useConversations';

/**
 * PODE ESCREVER NESTA CONVERSA?
 *
 * ⛔ Douglas, 17/09/2026: "não era para estar aparecendo 'verificando quem
 * atende'. Isso não tem que verificar, isso já tem que ter no banco salvo, não
 * pode ter isso de consulta."
 *
 * Ele está certo, e o custo era real: `staleTime: 0` fazia a ida ao servidor
 * em CADA clique de conversa — abrir, voltar e abrir de novo eram três idas —
 * e `refetchInterval: 15000` repetia a pergunta a cada 15 segundos enquanto a
 * conversa ficasse aberta. Enquanto a resposta não chegava, o rodapé exibia
 * "Verificando permissão de atendimento" e o campo ficava travado.
 *
 * O QUE MUDOU
 * 1. A resposta ÓBVIA sai do dado que a lista JÁ trouxe: pela Lei do Dono no
 *    Inbox, quem é dono escreve. `owner_user_id === eu` é decidido na hora,
 *    sem rede, e o rodapé nem chega a piscar.
 * 2. O servidor continua sendo a verdade — ele corrige casos que o front não
 *    conhece (janela de 24h, conexão caída, IA no meio). Só deixou de ser o
 *    ÚNICO caminho e de ser perguntado o tempo todo.
 * 3. `staleTime` de 30 s e sem `refetchInterval`: reabrir a mesma conversa não
 *    pergunta de novo, e conversa aberta parada não faz mais 4 idas por minuto.
 *
 * ⚠️ O palpite local só diz SIM no caso em que a lei é clara (sou o dono). Ele
 * nunca inventa um SIM onde o servidor diria não: para qualquer outro caso a
 * resposta continua vindo de lá. Escrever sem poder é pior que esperar.
 */
export function useWhatsAppWritePermission(conversation: Conversation | null, isGroup: boolean) {
  const { profile } = useAuth();

  const souDono = !!conversation?.owner_user_id
    && !!profile?.id
    && conversation.owner_user_id === profile.id;

  return useQuery({
    queryKey: ['whatsapp-write-permission', conversation?.id, profile?.id, conversation?.owner_user_id,
      conversation?.agente_ia_atendendo, conversation?.lead_id, conversation?.cliente_cnpj, conversation?.status, conversation?.liberada_consultor],
    enabled: !!conversation?.id && !!profile?.id && !isGroup,
    queryFn: () => getConversationPermission(conversation!.id),
    // Enquanto o servidor responde, o dono já escreve. Quem não é dono espera,
    // como antes — a diferença é que o dono é a maioria dos cliques.
    placeholderData: souDono
      ? ({
          // `assumir` vai junto porque o tipo tem os dois: quem já é dono não
          // precisa assumir de novo.
          assumir: { permitido: false, mensagem: 'Você já é o dono desta conversa.' },
          enviar: { permitido: true },
        } satisfies PermissaoConversa)
      : undefined,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}
